import React, { useState, useEffect } from 'react';
import { useToast } from '@/components/shared/ToastProvider';
import { resetEmailConfirmation, deleteUser, getEnvVariables } from '@/api';
import getAxiosErrors from '@/utils/getAxiosErrors';
import Button from '@/components/shared/Button';
import './DevelopmentPage.scss';

const baseClass = 'DevelopmentPage';

const DevelopmentPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [envVariables, setEnvVariables] = useState<Record<string, string>>({});
  const [isLoadingEnv, setIsLoadingEnv] = useState(false);
  const [envError, setEnvError] = useState<string | null>(null);

  const { showSuccess, showError } = useToast();

  useEffect(() => {
    const fetchEnvVariables = async () => {
      setIsLoadingEnv(true);
      setEnvError(null);
      try {
        const response = await getEnvVariables();
        setEnvVariables(response.data);
      } catch (err: any) {
        const errorData = getAxiosErrors(err);
        setEnvError(errorData.message || 'Failed to fetch environment variables.');
      } finally {
        setIsLoadingEnv(false);
      }
    };

    fetchEnvVariables();
  }, []);

  const handleResetEmail = async () => {
    if (!window.confirm(`Are you sure you want to reset email confirmation for ${email}?`)) {
      return;
    }
    setIsSubmitting(true);
    try {
      await resetEmailConfirmation(email);
      showSuccess('Email confirmation has been reset.');
      setEmail('');
    } catch (err: any) {
      const errorData = getAxiosErrors(err);
      showError(errorData.message || 'Failed to reset email confirmation.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!window.confirm(`Are you sure you want to permanently delete the user ${email}? This action cannot be undone.`)) {
      return;
    }
    setIsSubmitting(true);
    try {
      await deleteUser(email);
      showSuccess('User has been deleted.');
      setEmail('');
    } catch (err: any) {
      const errorData = getAxiosErrors(err);
      showError(errorData.message || 'Failed to delete user.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={baseClass}>
      <h1 className={`${baseClass}__title`}>Development Tools</h1>
      <p>This page is for testing system functions. Use with caution.</p>

      <div className={`${baseClass}__form`}>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Enter user email"
          className={`${baseClass}__input`}
          disabled={isSubmitting}
        />
        <div className={`${baseClass}__actions`}>
          <Button
            onClick={handleResetEmail}
            isDisabled={!email || isSubmitting}
            isLoading={isSubmitting}
          >
            Reset Email Confirmation
          </Button>
          <Button
            onClick={handleDeleteUser}
            isDisabled={!email || isSubmitting}
            isLoading={isSubmitting}
            theme="danger"
          >
            Delete User
          </Button>
        </div>
      </div>

      <div className={`${baseClass}__section`}>
        <h2>Backend Environment Variables</h2>
        {isLoadingEnv && <p>Loading environment variables...</p>}
        {envError && <p className={`${baseClass}__error`}>Error: {envError}</p>}
        {!isLoadingEnv && !envError && Object.keys(envVariables).length > 0 && (
          <pre className={`${baseClass}__env-display`}>
            {JSON.stringify(envVariables, null, 2)}
          </pre>
        )}
        {!isLoadingEnv && !envError && Object.keys(envVariables).length === 0 && (
          <p>No environment variables found or accessible.</p>
        )}
      </div>
    </div>
  );
};

export default DevelopmentPage;
